import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { AdminBroadcastService } from '../../../../shared/services/admin-broadcast.service';
import { AdminDashboardPageService } from '../admin-dashboard-page.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { BroadcastTarget, IAdminUser, UserRole } from '../../../../../assets/types/dtoTypes';

@Component({
  selector: 'app-broadcast-email-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './broadcast-email-page.component.html',
  styleUrls: ['./broadcast-email-page.component.scss'],
})
export class BroadcastEmailPageComponent {
  private readonly broadcastService = inject(AdminBroadcastService);
  private readonly adminService = inject(AdminDashboardPageService);
  private readonly popup = inject(ShowPopupService);

  protected readonly BroadcastTarget = BroadcastTarget;

  subject = '';
  content = '';
  target: BroadcastTarget = BroadcastTarget.ALL_CANDIDATES;

  candidates = signal<IAdminUser[]>([]);
  candidatesLoaded = signal<boolean>(false);
  loadingCandidates = signal<boolean>(false);
  search = signal<string>('');
  selectedIds = signal<Set<string>>(new Set<string>());
  sending = signal<boolean>(false);

  filteredCandidates = computed(() => {
    const q = this.search().trim().toLowerCase();
    const list = this.candidates();
    if (!q) return list;
    return list.filter(u =>
      `${u.firstname ?? ''} ${u.lastname ?? ''}`.toLowerCase().includes(q)
      || (u.email ?? '').toLowerCase().includes(q));
  });

  withApplicationCandidates = computed(() =>
    this.candidates().filter(u => (u.applicationsCount ?? 0) > 0)
  );

  get canSend(): boolean {
    if (!this.subject.trim() || !this.content.trim() || this.sending()) return false;
    if (this.target === BroadcastTarget.SELECTED) return this.selectedIds().size > 0;
    return true;
  }

  onTargetChange(): void {
    const needsCandidates =
      this.target === BroadcastTarget.SELECTED ||
      this.target === BroadcastTarget.WITH_APPLICATION;
    if (needsCandidates && !this.candidatesLoaded()) {
      this.loadCandidates();
    }
  }

  private loadCandidates(): void {
    this.loadingCandidates.set(true);
    this.adminService.getAdminUsers()
      .pipe(finalize(() => this.loadingCandidates.set(false)))
      .subscribe({
        next: (users) => {
          this.candidates.set(users.filter(u => u.role === UserRole.USER));
          this.candidatesLoaded.set(true);
        },
        error: () => this.popup.showFailurePopup({ title: 'Error', message: 'Could not load candidates.' }),
      });
  }

  toggle(id: string): void {
    const next = new Set(this.selectedIds());
    if (next.has(id)) next.delete(id); else next.add(id);
    this.selectedIds.set(next);
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  selectAllFiltered(): void {
    const next = new Set(this.selectedIds());
    this.filteredCandidates().forEach(u => next.add(u.id));
    this.selectedIds.set(next);
  }

  clearSelection(): void {
    this.selectedIds.set(new Set<string>());
  }

  send(): void {
    if (!this.canSend) return;
    let audience: string;
    let userIds: string[] | undefined;

    if (this.target === BroadcastTarget.SELECTED) {
      audience = `${this.selectedIds().size} selected user(s)`;
      userIds = Array.from(this.selectedIds());
    } else if (this.target === BroadcastTarget.WITH_APPLICATION) {
      audience = `${this.withApplicationCandidates().length} user(s) with at least one application`;
      userIds = this.withApplicationCandidates().map(u => u.id);
    } else {
      audience = 'ALL candidates';
    }

    this.popup.showConfirmationPopup({
      status: 'warning',
      translationKey: '',
      title: 'Send broadcast email?',
      message: `This will email ${audience}. This cannot be undone.`,
    }).subscribe((ok) => {
      if (!ok) return;
      this.sending.set(true);
      this.broadcastService.sendBroadcast({
        subject: this.subject.trim(),
        content: this.content,
        target: this.target,
        userIds,
      })
        .pipe(finalize(() => this.sending.set(false)))
        .subscribe({
          next: (res) => {
            this.popup.showSuccessPopup({ title: 'Email sent', message: `Delivered to ${res.recipients} recipient(s).` });
            this.resetForm();
          },
          error: (err) => this.popup.showFailurePopup({
            title: 'Error',
            message: err?.error?.message || 'Failed to send the broadcast.',
          }),
        });
    });
  }

  private resetForm(): void {
    this.subject = '';
    this.content = '';
    this.target = BroadcastTarget.ALL_CANDIDATES;
    this.selectedIds.set(new Set<string>());
    this.search.set('');
  }
}
